<script setup lang="ts">
import { nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useJobTypesStore } from '@/@vxapp/JobTypes/useJobTypesStore'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import JobTypeDialogNew from '@/components/Dialogs/JobTypes/JobTypeDialogNew.vue'
import ServiceListToolbar from '@/components/ServiceLists/Shared/ServiceListToolbar.vue'
import JobTypesDatatable from '@/components/Tables/JobTypes/JobTypesDatatable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'

type JobTypeData = Record<string, unknown>
type DisplayType = (typeof DialogDisplayType)[keyof typeof DialogDisplayType]
type DialogVisibilityPayload = boolean | { visibility: boolean }

definePage({ meta: PageMeta.JobTypes.list.meta })
setPageTitle(PageMeta.JobTypes.list.title)

const log = createLogger('JobTypesListPage')
const route = useRoute()
const router = useRouter()
const jobTypesStore = useJobTypesStore() as any
const jobTypesTable = useTemplateRef<InstanceType<typeof JobTypesDatatable>>('jobTypesTable')
const jobTypeData = ref<JobTypeData | null>(null)
const displayType = shallowRef<DisplayType>(DialogDisplayType.CREATE)
const isDialogVisible = shallowRef(false)
const resultCount = shallowRef(0)

const defaults: PaginationQueryParams = {
  sort: '-id',
  page: PaginationField.QueryParams.page,
  limit: PaginationField.QueryParams.limit,
  search: undefined,
}

const limitItems = [10, 25, 50, 100]

const sortItems = [
  { title: 'Newest records', value: '-id' },
  { title: 'Oldest records', value: '+id' },
]

function scalar(value: unknown) {
  const normalized = Array.isArray(value) ? value[0] : value

  return typeof normalized === 'string' || typeof normalized === 'number'
    ? String(normalized)
    : undefined
}

function positiveInteger(value: unknown, fallback: number) {
  const parsed = Number.parseInt(scalar(value) ?? '', 10)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

function normalize(source: Record<string, unknown>): PaginationQueryParams {
  const limit = positiveInteger(source.limit, defaults.limit)
  const search = scalar(source.search)?.trim()

  return {
    ...defaults,
    page: positiveInteger(source.page, defaults.page),
    limit: limitItems.includes(limit) ? limit : defaults.limit,
    sort: scalar(source.sort) === '+id' ? '+id' : '-id',
    search: search || undefined,
  }
}

function same(left: PaginationQueryParams, right: PaginationQueryParams) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search
  )
}

function applyRoute() {
  const incoming = normalize(route.query as Record<string, unknown>)
  const current = normalize(jobTypesStore.queryParams ?? defaults)

  if (same(current, incoming)) return false
  jobTypesStore.queryParams = incoming

  return true
}

function routeQuery(source: Record<string, unknown>) {
  const query = normalize(source)

  return {
    ...(query.page !== defaults.page ? { page: String(query.page) } : {}),
    ...(query.limit !== defaults.limit ? { limit: String(query.limit) } : {}),
    ...(query.sort !== defaults.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
  }
}

async function fetchJobTypes() {
  await jobTypesTable.value?.fetchJobTypes()
}

async function updateQuery(patch: Partial<PaginationQueryParams>) {
  const next = normalize({ ...jobTypesStore.queryParams, ...patch })

  jobTypesStore.queryParams = next

  const query = routeQuery({ ...next })

  if (JSON.stringify(route.query) !== JSON.stringify(query)) await router.replace({ query })
  await nextTick()
  await fetchJobTypes()
}

async function handleSearch(searchQuery: string) {
  await updateQuery({ search: searchQuery || undefined, page: 1 })
}

async function handleClear() {
  await updateQuery({ ...defaults })
}

async function handleCreate() {
  const { template, responseCode } = await jobTypesStore.fetchTemplate()

  if (responseCode !== ResponseCode.OK) return
  jobTypeData.value = cloneReactiveData(template ?? {})
  displayType.value = DialogDisplayType.CREATE
  isDialogVisible.value = true
}

function handleDialogVisibility(payload: DialogVisibilityPayload) {
  isDialogVisible.value = typeof payload === 'boolean' ? payload : payload.visibility
}

async function handleEdit(jobTypeGUID: string) {
  const { jobType, responseCode } = await jobTypesStore.fetch(jobTypeGUID)

  if (responseCode !== ResponseCode.OK) {
    log.error('Failed to load job type', { jobTypeGUID, responseCode })

    return
  }
  jobTypeData.value = cloneReactiveData(jobType ?? {})
  displayType.value = DialogDisplayType.EDIT
  isDialogVisible.value = true
}

async function handleSave(payload: JobTypeData) {
  const response =
    displayType.value === DialogDisplayType.CREATE
      ? await jobTypesStore.create(payload)
      : await jobTypesStore.update(payload)

  if (response.responseCode === ResponseCode.CREATED && response.jobType?.jobTypeGUID) {
    await router.push({
      name: 'setup-job-types-preview-guid',
      params: { guid: response.jobType.jobTypeGUID },
    })

    return
  }
  if (response.responseCode === ResponseCode.OK) await fetchJobTypes()
}

async function handlePreview(jobTypeGUID: string) {
  await router.push({ name: 'setup-job-types-preview-guid', params: { guid: jobTypeGUID } })
}

async function handleSort(value: unknown) {
  await updateQuery({ sort: String(value), page: 1 })
}

async function handleLimit(value: unknown) {
  await updateQuery({ limit: Number(value), page: 1 })
}

async function handlePage(page: number) {
  await updateQuery({ page })
}

function handleLoaded(count: number) {
  resultCount.value = count
}

jobTypesStore.queryParams = { ...defaults }
applyRoute()
onMounted(fetchJobTypes)

watch(
  () => route.query,
  async () => {
    if (!applyRoute()) return
    await nextTick()
    await fetchJobTypes()
  },
  { deep: true }
)
</script>

<template>
  <main
    class="job-types-workspace"
    data-testid="job-types-workspace"
  >
    <VCard class="job-types-workspace__hero">
      <VCardItem>
        <div class="job-types-workspace__hero-content">
          <div>
            <span class="job-types-workspace__eyebrow">Operations catalogue</span>
            <VCardTitle class="job-types-workspace__title">Job types</VCardTitle>
            <VCardSubtitle class="job-types-workspace__subtitle">
              Maintain reusable job definitions, revenue routing, and service configuration.
            </VCardSubtitle>
          </div>
          <div class="job-types-workspace__metric">
            <span>Current view</span>
            <strong>{{ resultCount.toLocaleString() }}</strong>
          </div>
        </div>
      </VCardItem>
      <VDivider />
      <VCardActions class="job-types-workspace__actions">
        <VBtn
          color="primary"
          prepend-icon="tabler-plus"
          @click="handleCreate"
        >
          New job type
        </VBtn>
        <VBtn
          prepend-icon="tabler-refresh"
          variant="tonal"
          @click="fetchJobTypes"
        >
          Refresh
        </VBtn>
      </VCardActions>
    </VCard>

    <ServiceListToolbar
      :query-params="jobTypesStore.queryParams"
      :result-count="resultCount"
      :sort-items="sortItems"
      heading="Find the next job type to manage"
      limit-label="Job types per page"
      plural-label="job types"
      search-label="Search job types"
      search-placeholder="Name, description or revenue stream"
      singular-label="job type"
      sort-label="Sort job types"
      @clear="handleClear"
      @limit-change="handleLimit"
      @refresh="fetchJobTypes"
      @search="handleSearch"
      @sort-change="handleSort"
    />

    <JobTypesDatatable
      ref="jobTypesTable"
      :query-params="jobTypesStore.queryParams"
      @edit="handleEdit"
      @loaded="handleLoaded"
      @page-change="handlePage"
      @preview="handlePreview"
    />
  </main>

  <JobTypeDialogNew
    v-if="jobTypeData"
    :is-dialog-visible="isDialogVisible"
    :is-update="displayType === DialogDisplayType.EDIT"
    :job-type-data="jobTypeData"
    :title="displayType === DialogDisplayType.EDIT ? 'Edit Job Type' : 'Create New Job Type'"
    subtitle="Define the operational record and its calendar colour."
    @update:is-dialog-visible="handleDialogVisibility"
    @update="handleSave"
  />
</template>

<style scoped lang="scss">
.job-types-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.job-types-workspace > * {
  box-sizing: border-box;
  inline-size: 100%;
  max-inline-size: 100%;
  min-inline-size: 0;
}

.job-types-workspace__hero {
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background:
    radial-gradient(circle at 88% -30%, rgba(var(--v-theme-primary), 0.2), transparent 28rem),
    linear-gradient(110deg, rgba(var(--v-theme-surface-bright), 0.98), rgb(var(--v-theme-surface)));
  box-shadow: 0 18px 52px rgba(var(--v-theme-on-background), 0.06);
}

.job-types-workspace__hero-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-block: 0.4rem;
}

.job-types-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.6875rem;
  font-weight: 590;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.job-types-workspace__title {
  margin-block-start: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 590;
  letter-spacing: -0.04em;
}

.job-types-workspace__subtitle {
  max-inline-size: 48rem;
  margin-block-start: 0.65rem;
  white-space: normal;
}

.job-types-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.job-types-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.job-types-workspace__metric strong {
  font-size: 1.25rem;
  font-weight: 590;
}

.job-types-workspace__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
}

@media (max-width: 599px) {
  .job-types-workspace {
    gap: 1rem;
  }

  .job-types-workspace__hero-content {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }

  .job-types-workspace__metric {
    min-inline-size: 0;
  }

  .job-types-workspace__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .job-types-workspace__actions :deep(.v-btn) {
    min-block-size: 3rem;
    inline-size: 100%;
  }
}
</style>
